/**
 * String-template building blocks for marketing pages (pure; runs in the prerender too).
 */
import { cardTitleParts } from '../lib/cardFace.js';
import { iconFor, iconSvg, SUIT_ICONS } from '../lib/cardIcons.js';
import { SITE, suitOf } from './data.js';

/** Escape text for HTML element content and attribute values. */
/** @param {unknown} s */
export const esc = (s) =>
  String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** UI-only glyphs; everything else comes from the deck's own icon set (lib/cardIcons.js). */
const UI_ICONS = {
  arrow: '<path d="M5 12h14"/><path d="m13 6 6 6-6 6"/>',
  check: '<path d="m5 12.5 4.5 4.5L19 7.5"/>',
};
const ALIASES = { smile: 'heartSmile' };

/**
 * Inline SVG icon. Accepts a suit id (home/out/…), a deck icon name, or arrow/check.
 * @param {string} name
 * @param {string} [cls]
 */
export function icon(name, cls = 'mk-ico') {
  if (UI_ICONS[name]) {
    return `<svg class="${cls}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${UI_ICONS[name]}</svg>`;
  }
  const deckName = SUIT_ICONS[name] ?? ALIASES[name] ?? name;
  return iconSvg(deckName, { size: 24, weight: 1.6 }).replace('<svg ', `<svg class="${cls}" `);
}

/** Title size step (container-relative) so long names fit like the canvas auto-fit. */
/** @param {string} t */
const titleSize = (t) => (t.length <= 9 ? 'xl' : t.length <= 16 ? 'l' : t.length <= 26 ? 'm' : 's');

/**
 * A card face in HTML/CSS, laid out on the same 250×350 grid as `drawCardFace` in
 * lib/cardFace.js (and using the same per-card icons), so prerendered/2D cards match the deck.
 * @param {import('./data.js').Card} card
 * @param {{ tag?: string, href?: string, cls?: string, style?: string }} [opts]
 */
export function cardFace(card, { tag = 'div', href, cls = '', style = '' } = {}) {
  const suit = suitOf(card.suit);
  const { title, sub } = cardTitleParts(card);
  const foot = [
    card.dailyGrind
      ? `<span class="fpc-mark fpc-dg">${iconSvg('cup', { size: 24, weight: 1.9 })}<b>Daily Grind</b></span>`
      : '<span></span>',
    card.happinessTrio
      ? `<span class="fpc-mark fpc-ht"><b>Happiness Trio</b>${iconSvg('heartSmile', { size: 24, weight: 1.9 })}</span>`
      : card.suit === 'wild'
        ? '<span class="fpc-wildnote">play when life happens</span>'
        : '<span></span>',
  ].join('');
  const attrs = [
    `class="fpc fpc--${card.suit} ${cls}"`,
    href ? `href="${href}"` : '',
    style ? `style="${style}"` : '',
    `data-suit="${card.suit}"`,
    `data-dg="${card.dailyGrind ? 1 : 0}"`,
    `data-name="${esc(card.fullName.toLowerCase())}"`,
  ]
    .filter(Boolean)
    .join(' ');
  return `<${tag} ${attrs}>
  <span class="fpc-frame" aria-hidden="true"></span>
  <span class="fpc-suit" aria-hidden="true">${esc(suit.name)}</span>
  <span class="fpc-suit-ico" aria-hidden="true">${iconSvg(SUIT_ICONS[card.suit] ?? 'sparkle', { size: 24, weight: 2 })}</span>
  <span class="fpc-medal" aria-hidden="true">${iconSvg(iconFor(card), { size: 24, weight: 1.35 })}</span>
  <span class="fpc-title fpc-title--${titleSize(title)}">${esc(title)}${sub ? `<small>${esc(sub)}</small>` : ''}</span>
  <span class="fpc-orn" aria-hidden="true"></span>
  <span class="fpc-foot">${foot}</span>
</${tag}>`;
}

/** Card back (ink, FP monogram, gold hairline). */
export const cardBack = (cls = '', style = '') =>
  `<div class="fpc fpc-back ${cls}" style="${style}" aria-hidden="true"><span class="fpc-mono">FP</span></div>`;

/** Primary call to action. */
/** @param {string} [label] */
export const startCta = (label = 'Start your deck') =>
  `<a class="mk-btn mk-btn--primary" href="${SITE.startPath}">${esc(label)}${icon('arrow', 'mk-btn-ico')}</a>`;

const NAV = [
  ['/how-it-works', 'How it works'],
  ['/cards', 'The cards'],
  ['/about', 'About'],
];

/** @param {string} path */
function header(path) {
  const links = NAV.map(([href, label]) => {
    const on = path === href || (href !== '/' && path.startsWith(`${href}/`));
    return `<a href="${href}"${on ? ' aria-current="page"' : ''}>${label}</a>`;
  }).join('');
  return `<header class="mk-head">
  <div class="mk-head-in">
    <a class="mk-logo" href="/" aria-label="Fair Play Deck home">
      <span class="mk-logo-mark" aria-hidden="true"><i></i><i></i></span>
      <span class="mk-logo-word">Fair Play <em>Deck</em></span>
    </a>
    <nav class="mk-nav" aria-label="Main">${links}</nav>
    <a class="mk-btn mk-btn--small" href="${SITE.startPath}">Start</a>
  </div>
</header>`;
}

function footer() {
  return `<footer class="mk-foot">
  <div class="mk-foot-in">
    <div class="mk-foot-credit">
      <p class="mk-foot-title">Based on <a href="${SITE.bookUrl}" rel="noopener"><cite>Fair Play</cite></a> by Eve Rodsky.</p>
      <p>An unofficial prototype built for Eve Rodsky. Card text © Eve Rodsky, used with credit. Not affiliated with, or endorsed by, Fair Play, Hello Sunshine or the Fair Play Policy Institute.</p>
    </div>
    <nav class="mk-foot-nav" aria-label="Footer">
      <a href="/how-it-works">How it works</a>
      <a href="/cards">All 100 cards</a>
      <a href="/about">About this prototype</a>
      <a href="${SITE.eveUrl}" rel="noopener">everodsky.com</a>
      <a href="/signin">Sign in</a>
    </nav>
  </div>
</footer>`;
}

/**
 * Full page chrome around a page body.
 * @param {string} path
 * @param {string} body
 */
export const layout = (path, body) =>
  `<div class="mk" data-prerendered="${esc(path)}">${header(path)}<main id="main" class="mk-main">${body}</main>${footer()}</div>`;

/** Clamp text to `n` chars on a word boundary. */
/** @param {string} s @param {number} n */
export function clip(s, n) {
  const t = String(s ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (t.length <= n) return t;
  const cut = t.slice(0, n - 1);
  return `${cut.slice(0, cut.lastIndexOf(' ')).replace(/[,;:.\s]+$/, '')}…`;
}
